/**
 * BizNearYou Core Product Experience - Smart Opportunity & Personalization Helpers
 */

import { BusinessBusiness, EvidenceItem } from "../types";
import { normalizeTags } from "./tagUtils";

export function formatDateString(dateStr: string | null | undefined, includeTime = false): string {
  if (!dateStr || dateStr === "Unknown" || dateStr === "null" || dateStr === "undefined") return "Unknown";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "Unknown";
    
    const dateOptions: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
    if (includeTime) {
      dateOptions.hour = 'numeric';
      dateOptions.minute = '2-digit';
    }
    return d.toLocaleDateString('en-US', dateOptions);
  } catch (e) {
    return "Unknown";
  }
}

export function generateHiringExplanation(
  business: BusinessBusiness,
  prospect: any,
  signalType: HiringSignalType,
  directNegativeEvidence: string[] = []
): {
  signal_last_checked_at: string;
  signal_source_updated_at: string | null;
  signal_source_url: string | null;
  signal_evidence_summary: string;
  signal_evidence_items: EvidenceItem[];
  confidenceClass: 'confirmed' | 'possible' | 'weak' | 'missing' | 'negative';
} {
  const name = business.name || "This business";
  const cat = business.category || "local business";
  const tags = normalizeTags(business.opportunityTags);
  const prospectTags = normalizeTags(prospect?.tags);
  const allTags = Array.from(new Set([...tags, ...prospectTags]));
  const website = business.website;
  const hiringPageUrl = business.hiringPageUrl || (business.websiteAudit as any)?.hiringPageUrl || (business as any).hiring_page_url;
  const audit = business.websiteAudit;
  const phone = business.phone;
  const rating = business.rating;
  const reviewsCount = business.reviewsCount || 0;

  // Timestamps
  const lastCheckedAt = business.last_checked_at || prospect?.last_checked_at || new Date().toISOString();

  const sourceUpdatedAt: string | null =
    (business as any).source_updated_at ||
    (business as any).source_published_at ||
    (business as any).source_posted_at ||
    (business as any).last_hiring_update_date ||
    null;

  const sourceUrl = hiringPageUrl || website || `https://www.google.com/search?q=${encodeURIComponent(name + " " + (business.address || ""))}`;

  const evidenceItems: EvidenceItem[] = [];

  // Confirmed Hiring Evidence
  if (hiringPageUrl) {
    evidenceItems.push({
      type: "Confirmed Hiring Evidence",
      source_type: "Careers Page",
      detail: `Active careers page detected at ${hiringPageUrl} for ${name}`,
      source_url: hiringPageUrl,
      date: sourceUpdatedAt,
      confidence: "High"
    });
  }

  if (allTags.includes("Currently Hiring") || allTags.includes("Verified Opening")) {
    evidenceItems.push({
      type: "Confirmed Hiring Evidence",
      source_type: "Job Board",
      detail: `Verified active employment ad or job posting confirmed for ${name}`,
      source_url: sourceUrl,
      date: sourceUpdatedAt,
      confidence: "High"
    });
  }

  if (allTags.includes("Hiring Multiple Roles")) {
    evidenceItems.push({
      type: "Confirmed Hiring Evidence",
      source_type: "Careers Page",
      detail: `Multiple open position listings detected across operational departments`,
      source_url: sourceUrl,
      date: sourceUpdatedAt,
      confidence: "High"
    });
  }

  // Possible Hiring Indicators
  if (allTags.includes("Expanding Location") || allTags.includes("New Business") || allTags.includes("High Growth Local Business")) {
    evidenceItems.push({
      type: "Possible Hiring Indicator",
      source_type: "Google Business Information",
      detail: `Location expansion or fresh business registration in the ${cat} sector`,
      source_url: sourceUrl,
      date: sourceUpdatedAt,
      confidence: "Medium"
    });
  }

  if (allTags.includes("Seasonal Hiring Likely")) {
    evidenceItems.push({
      type: "Possible Hiring Indicator",
      source_type: "Structured Data",
      detail: `Operates in ${cat}, an industry with cyclic recruitment drives`,
      source_url: sourceUrl,
      date: sourceUpdatedAt,
      confidence: "Medium"
    });
  }

  if (rating && rating >= 4.2 && reviewsCount >= 25) {
    evidenceItems.push({
      type: "Possible Hiring Indicator",
      source_type: "Google Business Information",
      detail: `High customer feedback volume (${reviewsCount} reviews, ${rating.toFixed(1)}★) indicates capacity expansion and team growth`,
      source_url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name + " " + (business.address || ""))}`,
      date: lastCheckedAt,
      confidence: "Medium"
    });
  }

  if (audit && audit.bookingButtonFound) {
    evidenceItems.push({
      type: "Possible Hiring Indicator",
      source_type: "Recent Website Changes",
      detail: `Active online booking or appointment scheduling system on website`,
      source_url: website,
      date: sourceUpdatedAt,
      confidence: "Medium"
    });
  }

  // Weak Evidence
  if (website && !hiringPageUrl && !allTags.includes("Currently Hiring")) {
    evidenceItems.push({
      type: "Weak Evidence",
      source_type: "Business Website",
      detail: `Official website (${website}) is active, but no dedicated careers portal or job openings were found`,
      source_url: website,
      date: sourceUpdatedAt,
      confidence: "Low"
    });
  }

  if (phone) {
    evidenceItems.push({
      type: "Weak Evidence",
      source_type: "Google Business Information",
      detail: `Direct phone number (${phone}) is listed for direct inquiries`,
      source_url: sourceUrl,
      date: lastCheckedAt,
      confidence: "Low"
    });
  }

  // Missing Evidence / Negative Evidence
  if (!website) {
    evidenceItems.push({
      type: "Missing Evidence",
      source_type: "Digital Footprint Search",
      detail: `No official business website or careers portal discovered for ${name}`,
      source_url: null,
      date: lastCheckedAt,
      confidence: "Low"
    });
  }

  if (audit && audit.failed) {
    evidenceItems.push({
      type: "Missing Evidence",
      source_type: "Recent Website Changes",
      detail: `Website scan was unreachable or failed to return readable HTML markup`,
      source_url: website,
      date: sourceUpdatedAt,
      confidence: "Low"
    });
  }

  if (directNegativeEvidence.length > 0) {
    for (const neg of directNegativeEvidence) {
      evidenceItems.push({
        type: "Negative Evidence",
        source_type: "Direct Verification",
        detail: neg,
        source_url: sourceUrl,
        date: sourceUpdatedAt,
        confidence: "High"
      });
    }
  }

  let summary = "";
  let confidenceClass: 'confirmed' | 'possible' | 'weak' | 'missing' | 'negative' = 'missing';

  if (signalType === "high") {
    confidenceClass = "confirmed";
    summary = `Confirmed hiring evidence was detected for ${name} (${cat}). ${
      hiringPageUrl 
        ? `An active careers portal was verified at ${hiringPageUrl}.` 
        : `Verified job postings and recruitment tags confirm an active staffing effort.`
    } BizNearYou confirmed this active hiring posture.`;
  } else if (signalType === "possible") {
    confidenceClass = "possible";
    const indicators = evidenceItems.filter(i => i.type === "Possible Hiring Indicator").map(i => i.detail);
    summary = `Possible hiring indicators identified for ${name}. While an explicit job ad was not confirmed, ${
      indicators.length > 0 
        ? indicators.slice(0, 2).join("; ") + "." 
        : `commercial growth and customer activity in the ${cat} sector suggest potential staffing demand.`
    } Direct outreach is recommended to confirm unlisted openings.`;
  } else if (signalType === "low") {
    confidenceClass = "negative";
    summary = `Hiring signal is classified as Low for ${name}. Direct evidence confirms recruitment is inactive or closed (${directNegativeEvidence.join("; ") || "explicit status indicates no openings"}).`;
  } else if (signalType === "improbable") {
    confidenceClass = "negative";
    summary = `Hiring signal is classified as Improbable for ${name}. Analysis identified negative indicators such as stale recruitment information or reduced operational activity.`;
  } else {
    confidenceClass = evidenceItems.some(i => i.type === "Weak Evidence") ? "weak" : "missing";
    summary = `Hiring signal remains unconfirmed for ${name}. ${
      website 
        ? `The official website (${website}) was analyzed, but no active careers page, job posting, or verified hiring form was discovered.` 
        : `No official website or online job listings were found.`
    } Direct contact with the business is required to verify current staffing needs.`;
  }

  return {
    signal_last_checked_at: lastCheckedAt,
    signal_source_updated_at: sourceUpdatedAt,
    signal_source_url: sourceUrl,
    signal_evidence_summary: summary,
    signal_evidence_items: evidenceItems,
    confidenceClass
  };
}

export interface Interaction {
  id: string;
  businessId: string;
  category: string;
  eventType: 'view' | 'save' | 'unsave' | 'contact_click' | 'website_click' | 'pipeline_change';
  timestamp: number;
}

export interface UserStats {
  streak: number;
  lastActiveDate: string; // YYYY-MM-DD
  total_points?: number;
  monthlyMilestones?: {
    monthKey: string;
    activeDaysCount: number;
    badgesUnlockedCount: number;
  };
  dailyGoals: {
    reviewed: number; // goal: 5
    saved: number; // goal: 2
    contacted: number; // goal: 1
    followUp: number; // goal: 1
  };
  achievements: {
    id: string;
    title: string;
    description: string;
    unlockedAt: string | null;
    icon: string;
    rarity: 'Common' | 'Uncommon' | 'Rare' | 'Epic' | 'Legendary';
    isSecret?: boolean;
    target?: number;
  }[];
  claimedDailyBonus?: boolean;
  lifetime_searches?: number;
  lifetime_saves?: number;
  lifetime_contacts?: number;
  lifetime_audits?: number;
  lifetime_hiring_signals?: number;
  lifetime_careers_pages?: number;
  lifetime_no_website?: number;
  lifetime_ssl_risks?: number;
  lifetime_copies?: number;
  lifetime_phone_calls?: number;
  lifetime_directions?: number;
  lifetime_quests_completed?: number;
  lifetime_views?: number;
  session_views_timestamps?: number[];
  searched_locations?: string[];
}

const DEFAULT_ACHIEVEMENTS: {
  id: string;
  title: string;
  description: string;
  unlockedAt: string | null;
  icon: string;
  rarity: 'Common' | 'Uncommon' | 'Rare' | 'Epic' | 'Legendary';
  isSecret?: boolean;
  target?: number;
}[] = [
  // --- Beginner (7 Badges) ---
  { id: "quick_scan", title: "First Scan", description: "Launch your very first local business scouting query", unlockedAt: null, icon: "🔍", rarity: "Common" },
  { id: "curious_scout", title: "Curious Scout", description: "View the details of any local business target", unlockedAt: null, icon: "👀", rarity: "Common" },
  { id: "first_save", title: "Prospect Hunter", description: "Save your first business opportunity target", unlockedAt: null, icon: "🎯", rarity: "Common" },
  { id: "first_outreach", title: "Icebreaker", description: "Click to contact a business or copy their info for outreach", unlockedAt: null, icon: "💬", rarity: "Common" },
  { id: "filter_ninja", title: "Filter Ninja", description: "Use custom filters or categories to narrow targets", unlockedAt: null, icon: "🥷", rarity: "Common" },
  { id: "streak_3", title: "Consistency Champion", description: "Maintain a 3-day opportunity scouting streak", unlockedAt: null, icon: "⚡", rarity: "Uncommon", target: 3 },
  { id: "perfect_day", title: "Daily Perfectionist", description: "Complete today's scouting quest for the first time", unlockedAt: null, icon: "🌟", rarity: "Uncommon" },

  // --- Sourcing & CRM Progress (12 Badges) ---
  { id: "high_signal", title: "High-Signal Hunter", description: "Review 10+ high-score targets", unlockedAt: null, icon: "🔥", rarity: "Common", target: 10 },
  { id: "crm_master", title: "CRM Optimizer", description: "Advance 5 businesses along your CRM pipeline stages", unlockedAt: null, icon: "💼", rarity: "Uncommon", target: 5 },
  { id: "data_pro", title: "Outreach Master", description: "Perform 5 outreach/contact clicks in a single day", unlockedAt: null, icon: "📞", rarity: "Uncommon", target: 5 },
  { id: "expansionist", title: "Regional Tycoon", description: "Save 5+ business targets in a single day", unlockedAt: null, icon: "🏰", rarity: "Uncommon", target: 5 },
  { id: "pioneer", title: "Local Pioneer", description: "Search for opportunities in 3 different locations", unlockedAt: null, icon: "🗺️", rarity: "Uncommon", target: 3 },
  { id: "scouting_beast", title: "Sourcing Overlord", description: "Perform 15+ lifetime business opportunity searches", unlockedAt: null, icon: "👁️", rarity: "Uncommon", target: 15 },
  { id: "scouting_elite", title: "Scouting Elite", description: "Perform 50+ lifetime business opportunity searches", unlockedAt: null, icon: "🧭", rarity: "Rare", target: 50 },
  { id: "scouting_grandmaster", title: "Scouting Grandmaster", description: "Perform 150+ lifetime business opportunity searches", unlockedAt: null, icon: "🔮", rarity: "Legendary", target: 150 },
  { id: "hoarder", title: "Opportunity Hoarder", description: "Save 25+ lifetime business opportunity targets", unlockedAt: null, icon: "🗄️", rarity: "Rare", target: 25 },
  { id: "hoarder_legend", title: "Vault Keeper", description: "Save 100+ lifetime business opportunity targets", unlockedAt: null, icon: "🏛️", rarity: "Legendary", target: 100 },
  { id: "seo_specialist", title: "SEO Guru", description: "Run 5+ deep website audits to spot digital gaps", unlockedAt: null, icon: "📊", rarity: "Uncommon", target: 5 },
  { id: "contact_mogul", title: "Contact Tycoon", description: "Retrieve contacts for 5+ local business targets", unlockedAt: null, icon: "💡", rarity: "Uncommon", target: 5 },

  // --- Strategic/Skill-Based (8 Badges) ---
  { id: "hiring_sleuth", title: "Hiring Sleuth", description: "Find 5 businesses with High Hiring Signals", unlockedAt: null, icon: "🕵️", rarity: "Uncommon", target: 5 },
  { id: "careers_finder", title: "Careers Pathfinder", description: "Spot 3 businesses with active career or jobs pages", unlockedAt: null, icon: "🛣️", rarity: "Rare", target: 3 },
  { id: "digital_rescuer", title: "Digital Rescuer", description: "Find 10 businesses operating without an active website", unlockedAt: null, icon: "🏥", rarity: "Rare", target: 10 },
  { id: "secure_scout", title: "Security Sentinel", description: "Spot 5 businesses with SSL security issues or digital risks", unlockedAt: null, icon: "🛡️", rarity: "Uncommon", target: 5 },
  { id: "copy_ninja", title: "Copy Ninja", description: "Copy 10 personalized outreach messages or business info blocks", unlockedAt: null, icon: "📋", rarity: "Common", target: 10 },
  { id: "phone_dialer", title: "Cold Dialer", description: "Initiate 5 direct phone calls to target businesses", unlockedAt: null, icon: "☎️", rarity: "Uncommon", target: 5 },
  { id: "direction_scout", title: "Ground Scout", description: "Request maps directions to local business targets 5 times", unlockedAt: null, icon: "🚗", rarity: "Uncommon", target: 5 },
  { id: "booster_legend", title: "Booster King", description: "Claim a lucky mystery booster of 2+ hours", unlockedAt: null, icon: "🎒", rarity: "Rare" },

  // --- Elite & High Progression (6 Badges) ---
  { id: "outreach_champion", title: "Outreach Champion", description: "Perform 30+ lifetime outreach or contact clicks", unlockedAt: null, icon: "🏆", rarity: "Epic", target: 30 },
  { id: "outreach_god", title: "Dealmaker Envoy", description: "Perform 100+ lifetime outreach or contact clicks", unlockedAt: null, icon: "🤝", rarity: "Legendary", target: 100 },
  { id: "pro_champion", title: "Pro Elite Elite", description: "Unlock Pro Suite or claim your elite daily quest bonus", unlockedAt: null, icon: "💎", rarity: "Rare" },
  { id: "streak_7", title: "Unstoppable Force", description: "Maintain an elite 7-day scouting streak", unlockedAt: null, icon: "👑", rarity: "Rare", target: 7 },
  { id: "streak_15", title: "Local Legend", description: "Maintain an incredible 15-day opportunity scouting streak", unlockedAt: null, icon: "🌋", rarity: "Epic", target: 15 },
  { id: "streak_30", title: "Scouting Deity", description: "Achieve an legendary 30-day opportunity scouting streak", unlockedAt: null, icon: "🌌", rarity: "Legendary", target: 30 },

  // --- Quests (3 Badges) ---
  { id: "quest_conqueror", title: "Quest Conqueror", description: "Complete today's scouting quest 5 times", unlockedAt: null, icon: "⚔️", rarity: "Rare", target: 5 },
  { id: "quest_legend", title: "Scouting Myth", description: "Complete today's scouting quest 20 times", unlockedAt: null, icon: "🐉", rarity: "Legendary", target: 20 },

  // --- Secrets (3 Badges) ---
  { id: "secret_night_owl", title: "Night Owl Scout", description: "Scout for opportunities late at night (between 12 AM and 4 AM)", unlockedAt: null, icon: "🦉", rarity: "Epic", isSecret: true },
  { id: "secret_jackpot", title: "Scouting Jackpot", description: "Discover a business target with a perfect Discovery Score of 95+", unlockedAt: null, icon: "🎰", rarity: "Epic", isSecret: true },
  { id: "secret_speedrun", title: "Scouting Speedrun", description: "Review 5 different business opportunities in under 60 seconds", unlockedAt: null, icon: "⏱️", rarity: "Rare", isSecret: true },

  // --- Completionist (3 Badges) ---
  { id: "badge_master", title: "Badge Collector", description: "Unlock 10 or more distinct reputation badges", unlockedAt: null, icon: "🎖️", rarity: "Rare", target: 10 },
  { id: "completionist_epic", title: "Ascended Scout", description: "Unlock 20 distinct reputation badges", unlockedAt: null, icon: "🎖️", rarity: "Epic", target: 20 },
  { id: "completionist_legend", title: "Trophy Case Overlord", description: "Unlock 35 distinct reputation badges", unlockedAt: null, icon: "👑", rarity: "Legendary", target: 35 }
];

/**
 * Maps business category to Suggested Roles
 */
export function getSuggestedRoles(category: string, rawTagsInput: any = []): string[] {
  const catLower = (category || "").toLowerCase();
  const tags = normalizeTags(rawTagsInput);
  
  if (catLower.includes("restaurant") || catLower.includes("cafe") || catLower.includes("food") || catLower.includes("bakery") || catLower.includes("bar")) {
    return ["Social Media Marketer", "Delivery Coordinator", "Part-Time Crew Member", "Local SEO Optimizer"];
  }
  if (catLower.includes("dentist") || catLower.includes("dental") || catLower.includes("medical") || catLower.includes("clinic") || catLower.includes("doctor") || catLower.includes("care")) {
    return ["Customer Success Specialist", "SEO Content Assistant", "Front Desk Coordinator", "Lead Capture Specialist"];
  }
  if (catLower.includes("builder") || catLower.includes("contractor") || catLower.includes("construction") || catLower.includes("remodel") || catLower.includes("roofing") || catLower.includes("paint")) {
    return ["Local Lead Finder", "Client Coordinator", "Website Modernizer", "Operational Assistant"];
  }
  if (catLower.includes("salon") || catLower.includes("spa") || catLower.includes("hair") || catLower.includes("beauty") || catLower.includes("barber")) {
    return ["Instagram Growth Manager", "Booking Funnel Optimizer", "Front Desk Host", "Email Newsletter Assistant"];
  }
  if (catLower.includes("cleaning") || catLower.includes("laundry") || catLower.includes("moving") || catLower.includes("tow") || catLower.includes("automotive") || catLower.includes("mechanic")) {
    return ["Dispatch/Scheduling Assistant", "Local Ad Optimizer", "Review Acquisition Specialist", "Support Agent"];
  }
  if (catLower.includes("consulting") || catLower.includes("agency") || catLower.includes("software") || catLower.includes("tech") || catLower.includes("creative") || catLower.includes("marketing")) {
    return ["Junior Web Developer", "Copywriting Assistant", "Sales Outreach Associate", "Content Coordinator"];
  }
  if (catLower.includes("boutique") || catLower.includes("retail") || catLower.includes("store") || catLower.includes("shop") || catLower.includes("market")) {
    return ["E-Commerce Coordinator", "Social Media Content Creator", "Brand Ambassador", "Customer Assistant"];
  }

  // Default suggestions based on tags
  if (tags.includes("Currently Hiring") || tags.includes("High Hiring Signal")) {
    return ["Operational Assistant", "Customer Support Agent", "Marketing Assistant"];
  }
  return ["Freelance Website Designer", "Social Media Coordinator", "Lead Generation Specialist"];
}

/**
 * Calculates a descriptive, professional summary of why the business is worth contacting
 */
export function getWhyWorthContacting(category: string, rawTagsInput: any = [], score: number = 0): string {
  const cat = category || "local enterprise";
  const tags = normalizeTags(rawTagsInput);
  
  if (tags.includes("No Website Found")) {
    return `This high-scoring ${cat} operates without an active website, presenting an immediate opportunity to offer digital launching or landing page setup.`;
  }
  if (tags.includes("Website Needs Review") || tags.includes("Needs Website Cleanup") || tags.includes("SSL Security Risk")) {
    return `Their current website has critical technical, mobile, or security issues where clean redesigns will instantly recover client drop-offs.`;
  }
  if (tags.includes("Currently Hiring") || tags.includes("High Hiring Signal") || tags.includes("Hiring Multiple Roles")) {
    return `This business has strong operational momentum and clear staffing signals, making them an ideal target for job applications or hiring outreach.`;
  }
  if (tags.includes("Weak Reviews") || tags.includes("Reputation Repair Prospect") || tags.includes("Needs Review Help")) {
    return `With positive offerings but low public satisfaction reviews, they are a prime prospect for review management pipelines.`;
  }
  if (tags.includes("Untapped Growth Potential") || tags.includes("Under-Marketed Business")) {
    return `An outstanding rating score but very low review count suggests a high-quality local gem ready to scale with lead-generation services.`;
  }

  if (score >= 80) {
    return `A highly stable, prestigious local business with excellent engagement metrics, representing a top-tier premium contact opportunity.`;
  }
  
  return `A verified local ${cat} with active contact details, making them a quick and easy target for personalized local outreach.`;
}

/**
 * Rates the hiring likelihood for job seekers
 */
export function getHiringLikelihood(hiringScore: number, rawTagsInput: any = []): {
  level: 'High' | 'Medium' | 'Low';
  label: string;
  bg: string;
  text: string;
} {
  const tags = normalizeTags(rawTagsInput);
  const isHigh = 
    tags.includes("Currently Hiring") || 
    tags.includes("Hiring Multiple Roles") || 
    tags.includes("High Hiring Signal") || 
    hiringScore >= 70;
    
  if (isHigh) {
    return {
      level: 'High',
      label: '🔥 High hiring signal',
      bg: 'bg-amber-500/10 text-amber-700 border-amber-200',
      text: 'text-amber-700'
    };
  }

  const isMedium = 
    tags.includes("Seasonal Hiring Likely") || 
    tags.includes("Expanding Location") || 
    tags.includes("Ages 16+ Welcome") ||
    hiringScore >= 40;

  if (isMedium) {
    return {
      level: 'Medium',
      label: '✨ Possible hiring target',
      bg: 'bg-blue-500/10 text-blue-700 border-blue-200',
      text: 'text-blue-700'
    };
  }

  return {
    level: 'Low',
    label: 'Unconfirmed hiring',
    bg: 'bg-slate-100 text-slate-500 border-slate-200',
    text: 'text-slate-500'
  };
}

export type HiringSignalType =
  | "high"
  | "possible"
  | "unconfirmed"
  | "improbable"
  | "low";

export const HIRING_SIGNAL_RANK = {
  high: 5,
  possible: 4,
  unconfirmed: 3,
  improbable: 2,
  low: 1
};

export function refineUnconfirmedHiringSignal(
  business: BusinessBusiness,
  prospect: any,
  legacyResult: { level: 'High' | 'Medium' | 'Low'; label: string; bg: string; text: string; }
): {
  type: HiringSignalType;
  rank: number;
  label: string;
  legacyType: "high" | "possible" | "unconfirmed";
  reason: string;
  negativeEvidence: string[];
  directNegativeEvidence: string[];
  analysisCompleted: boolean;
  signal_last_checked_at: string;
  signal_source_updated_at: string | null;
  signal_source_url: string | null;
  signal_evidence_summary: string;
  signal_evidence_items: EvidenceItem[];
  confidenceClass: 'confirmed' | 'possible' | 'weak' | 'missing' | 'negative';
} {
  // 1. Immediately preserve positive legacy results
  if (legacyResult.level === 'High') {
    const exp = generateHiringExplanation(business, prospect, 'high', []);
    return {
      type: 'high',
      rank: 5,
      label: 'High Hiring Signal',
      legacyType: 'high',
      reason: 'Preserved High Hiring Signal from legacy system.',
      negativeEvidence: [],
      directNegativeEvidence: [],
      analysisCompleted: true,
      ...exp
    };
  }
  if (legacyResult.level === 'Medium') {
    const exp = generateHiringExplanation(business, prospect, 'possible', []);
    return {
      type: 'possible',
      rank: 4,
      label: 'Possible Hiring Signal',
      legacyType: 'possible',
      reason: 'Preserved Possible Hiring Signal from legacy system.',
      negativeEvidence: [],
      directNegativeEvidence: [],
      analysisCompleted: true,
      ...exp
    };
  }

  // legacyType is unconfirmed
  const legacyType = 'unconfirmed';

  // Extract variables
  const hiringStatus = (business.hiringStatus || "").toLowerCase().trim();
  const tags: string[] = normalizeTags(business.opportunityTags);
  const prospectTags = normalizeTags(prospect?.tags);
  for (const pt of prospectTags) {
    if (!tags.includes(pt)) tags.push(pt);
  }

  // Also collect evidence
  const evidence: string[] = [];
  if (prospect && Array.isArray(prospect.evidence)) {
    evidence.push(...prospect.evidence);
  }

  // Evaluate Low Hiring Signal first (rare, require strong direct evidence)
  const isClosed = 
    business.pipeline_stage === "Blocked" ||
    business.pipelineStage === "Blocked" ||
    (prospect?.categorizedSignals?.warnings || []).includes("Possibly Closed") ||
    tags.some(t => t.toLowerCase() === "permanently closed") ||
    hiringStatus === "permanently_closed" ||
    hiringStatus === "closed";

  const lowStatusValues = [
    "not_hiring",
    "confirmed_not_hiring",
    "no_current_openings",
    "hiring_closed",
    "hiring_freeze",
    "applications_closed"
  ];

  const lowTags = [
    "Confirmed Not Hiring",
    "Not Currently Hiring",
    "No Current Openings",
    "Hiring Closed",
    "Hiring Freeze",
    "Applications Closed",
    "Low Hiring Signal"
  ];

  // Direct negative evidence
  const directNegativeEvidence: string[] = [];
  if (lowStatusValues.includes(hiringStatus)) {
    directNegativeEvidence.push(`Explicit hiring status is "${business.hiringStatus}"`);
  }
  const matchingLowTags = tags.filter(t => lowTags.some(lt => lt.toLowerCase() === t.toLowerCase()));
  if (matchingLowTags.length > 0) {
    directNegativeEvidence.push(`Has explicit tag(s): ${matchingLowTags.join(", ")}`);
  }
  if (isClosed) {
    directNegativeEvidence.push("Business is permanently closed or blocked");
  }

  // A current verified statement on careers page or trusted source
  const hasNoOpeningsCareersStatement = 
    tags.some(t => t.toLowerCase().includes("careers page says no openings") || t.toLowerCase().includes("no openings on careers page")) ||
    evidence.some(e => e.toLowerCase().includes("no openings") || e.toLowerCase().includes("not accepting applications"));
  if (hasNoOpeningsCareersStatement) {
    directNegativeEvidence.push("Verified statement indicates no openings or applications are not being accepted");
  }

  // Check if there's newer active opening evidence that might override
  const hasActiveOpeningEvidence = 
    tags.includes("Currently Hiring") || 
    tags.includes("Hiring Multiple Roles") || 
    tags.includes("High Hiring Signal") ||
    hiringStatus === "active" ||
    hiringStatus === "hiring";

  if (directNegativeEvidence.length > 0 && !hasActiveOpeningEvidence) {
    const exp = generateHiringExplanation(business, prospect, 'low', directNegativeEvidence);
    return {
      type: 'low',
      rank: 1,
      label: 'Low Hiring Signal',
      legacyType: 'unconfirmed',
      reason: `Direct evidence confirms hiring is unavailable: ${directNegativeEvidence.join("; ")}`,
      negativeEvidence: directNegativeEvidence,
      directNegativeEvidence,
      analysisCompleted: true,
      ...exp
    };
  }

  // Evaluate Improbable Hiring Signal
  // Condition A: Trusted structured status explicitly indicates improbable_hiring, unlikely_hiring, weak_hiring_likelihood
  const improbableStatusValues = [
    "improbable_hiring",
    "unlikely_hiring",
    "weak_hiring_likelihood"
  ];

  if (improbableStatusValues.includes(hiringStatus) && !hasActiveOpeningEvidence) {
    const exp = generateHiringExplanation(business, prospect, 'improbable', [`Explicit status: ${business.hiringStatus}`]);
    return {
      type: 'improbable',
      rank: 2,
      label: 'Improbable Hiring Signal',
      legacyType: 'unconfirmed',
      reason: `Trusted structured status explicitly indicates: ${business.hiringStatus}`,
      negativeEvidence: [`Explicit status: ${business.hiringStatus}`],
      directNegativeEvidence: [],
      analysisCompleted: true,
      ...exp
    };
  }

  // Condition B: successfully analyzed and has at least two independent negative indicators
  const analysisCompleted = 
    business.crawlStatus === 'success' || 
    (business.websiteAudit !== undefined && business.websiteAudit !== null && !business.websiteAudit.failed && business.websiteAudit.exists !== false);

  const negativeEvidence: string[] = [];

  // Let's identify independent negative signals:
  let hasStaleRecruitment = false;
  let hasInactiveCareers = false;
  let hasReducedOperations = false;
  let hasLowScoreWithNegEvidence = false;

  // 1. stale hiring or recruitment info
  const staleTags = ["Stale Hiring", "Stale Hiring Information", "Outdated Recruitment", "Stale Recruitment", "Expired Recruitment", "Outdated Hiring Content", "Outdated Job Postings", "Outdated Careers Page", "Expired Job Openings", "Outdated Hiring"];
  if (tags.some(t => staleTags.some(st => st.toLowerCase() === t.toLowerCase())) || 
      hiringStatus === "stale_hiring" || hiringStatus === "expired_hiring" || hiringStatus === "outdated_hiring" ||
      (business.websiteAudit?.outdatedDetails === true)) {
    hasStaleRecruitment = true;
    negativeEvidence.push("Stale/expired hiring or recruitment information detected");
  }

  // 2. previously active careers page that is now inactive
  const inactiveCareersTags = ["Inactive Careers Page", "Careers Page Inactive", "Previously Active Careers Page Inactive", "Inactive Careers"];
  if (tags.some(t => inactiveCareersTags.some(ict => ict.toLowerCase() === t.toLowerCase())) || 
      hiringStatus === "inactive_careers_page" || hiringStatus === "careers_inactive") {
    hasInactiveCareers = true;
    negativeEvidence.push("Previously active careers page is now inactive");
  }

  // 3. verified reduction in staffing demand / reducing operations / downsizing
  const reducedOpsTags = ["Staffing Demand Reduction", "Reduced Staffing", "Reduction in Staffing Demand", "Reducing Operations", "Operations Reduced", "Downsizing", "Operations Downsized", "Staff Reduced"];
  if (tags.some(t => reducedOpsTags.some(rot => rot.toLowerCase() === t.toLowerCase())) || 
      hiringStatus === "staff_reduction" || hiringStatus === "reducing_operations" || hiringStatus === "downsizing" ||
      (business.jobEnrichment?.jobSecurity?.financialHealth === "declining")) {
    hasReducedOperations = true;
    negativeEvidence.push("Verified reduction in staffing demand or operations (downsizing)");
  }

  // 4. sufficiently low existing hiring score combined with actual negative evidence
  const hiringScore = (business as any).scores?.hiringScore ?? prospect?.scores?.hiringScore ?? (business as any).hiringScore ?? 0;
  if (hiringScore < 35 && (hasStaleRecruitment || hasInactiveCareers || hasReducedOperations || 
      (business.jobEnrichment?.jobSecurity?.securityIndicator === "low") || 
      (business.jobEnrichment?.jobSecurity?.concerns && business.jobEnrichment.jobSecurity.concerns.length > 0))) {
    hasLowScoreWithNegEvidence = true;
    negativeEvidence.push(`Low hiring score (${hiringScore}) combined with negative signals`);
  }

  // Count independent signals
  let independentSignalsCount = 0;
  if (hasStaleRecruitment) independentSignalsCount++;
  if (hasInactiveCareers) independentSignalsCount++;
  if (hasReducedOperations) independentSignalsCount++;
  if (hasLowScoreWithNegEvidence) independentSignalsCount++;

  if (analysisCompleted && independentSignalsCount >= 2 && !hasActiveOpeningEvidence) {
    const exp = generateHiringExplanation(business, prospect, 'improbable', negativeEvidence);
    return {
      type: 'improbable',
      rank: 2,
      label: 'Improbable Hiring Signal',
      legacyType: 'unconfirmed',
      reason: `Analysis found ${independentSignalsCount} independent negative signals: ${negativeEvidence.join(", ")}`,
      negativeEvidence,
      directNegativeEvidence: [],
      analysisCompleted: true,
      ...exp
    };
  }

  // Default remains Unconfirmed
  const exp = generateHiringExplanation(business, prospect, 'unconfirmed', []);
  return {
    type: 'unconfirmed',
    rank: 3,
    label: 'Unconfirmed Hiring Signal',
    legacyType: 'unconfirmed',
    reason: 'Hiring signal remains unconfirmed (insufficient negative or positive evidence).',
    negativeEvidence,
    directNegativeEvidence: [],
    analysisCompleted,
    ...exp
  };
}

/**
 * Handles saving and retrieving user statistics & gamified progress in localStorage, isolated by user ID.
 */
export function getUserStats(userId?: string): UserStats {
  const cacheKey = userId ? `biznearyou_user_stats_${userId}` : "biznearyou_user_stats_v1";
  const defaultStats: UserStats = {
    streak: 1,
    lastActiveDate: new Date().toISOString().split('T')[0],
    dailyGoals: {
      reviewed: 0,
      saved: 0,
      contacted: 0,
      followUp: 0
    },
    achievements: DEFAULT_ACHIEVEMENTS,
    claimedDailyBonus: false
  };

  try {
    const data = localStorage.getItem(cacheKey);
    if (!data) {
      if (userId) {
        localStorage.setItem(cacheKey, JSON.stringify(defaultStats));
      }
      return defaultStats;
    }
    const parsed = JSON.parse(data);
    
    // Validate schema
    if (!parsed.dailyGoals || !parsed.achievements || !Array.isArray(parsed.achievements)) {
      if (userId) {
        localStorage.setItem(cacheKey, JSON.stringify(defaultStats));
      }
      return defaultStats;
    }

    // Ensure backward compatibility by adding missing achievements
    const existingIds = new Set(parsed.achievements.map((a: any) => a.id));
    const missingAchievements = DEFAULT_ACHIEVEMENTS.filter(a => !existingIds.has(a.id));
    if (missingAchievements.length > 0) {
      parsed.achievements = [...parsed.achievements, ...missingAchievements];
    }
    
    // Sync any updated description or titles from DEFAULT_ACHIEVEMENTS
    parsed.achievements = parsed.achievements.map((a: any) => {
      const found = DEFAULT_ACHIEVEMENTS.find(da => da.id === a.id);
      if (found) {
        return { ...found, unlockedAt: a.unlockedAt };
      }
      return a;
    });

    // Check and update daily streak
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    
    // Monthly milestones reset check and initialization
    const currentMonthKey = today.slice(0, 7); // "YYYY-MM"
    if (!parsed.monthlyMilestones || parsed.monthlyMilestones.monthKey !== currentMonthKey) {
      parsed.monthlyMilestones = {
        monthKey: currentMonthKey,
        activeDaysCount: parsed.monthlyMilestones ? 0 : 1, // initialize to 1 for first run, or reset to 0 on new month
        badgesUnlockedCount: 0
      };
    }

    if (parsed.lastActiveDate !== today) {
      if (parsed.lastActiveDate === yesterday) {
        parsed.streak += 1;
      } else if (parsed.lastActiveDate !== today) {
        parsed.streak = 1; // streak reset
      }
      
      // Increment monthly active days count on new active day
      parsed.monthlyMilestones.activeDaysCount = (parsed.monthlyMilestones.activeDaysCount || 0) + 1;
      
      // Reset daily goals for the new day
      parsed.dailyGoals = { reviewed: 0, saved: 0, contacted: 0, followUp: 0 };
      parsed.claimedDailyBonus = false;
      parsed.lastActiveDate = today;
      if (userId) {
        localStorage.setItem(cacheKey, JSON.stringify(parsed));
      }
    }

    // Dynamically calculate and update monthly badge count to keep it always accurate and in-sync
    let currentMonthBadgesCount = 0;
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    if (parsed.achievements && Array.isArray(parsed.achievements)) {
      parsed.achievements.forEach((ach: any) => {
        if (ach.unlockedAt) {
          try {
            const unlockedDate = new Date(ach.unlockedAt);
            if (!isNaN(unlockedDate.getTime())) {
              if (unlockedDate.getMonth() === currentMonth && unlockedDate.getFullYear() === currentYear) {
                currentMonthBadgesCount++;
              }
            }
          } catch (err) {
            console.warn("Failed to parse unlockedAt date:", ach.unlockedAt, err);
          }
        }
      });
    }
    parsed.monthlyMilestones.badgesUnlockedCount = currentMonthBadgesCount;

    // Calculate total points from unlocked badges to guarantee QP/XP points and rank are never lost
    let computedBadgePoints = 0;
    if (parsed.achievements && Array.isArray(parsed.achievements)) {
      parsed.achievements.forEach((ach: any) => {
        if (ach.unlockedAt) {
          switch (ach.rarity) {
            case "Common": computedBadgePoints += 50; break;
            case "Uncommon": computedBadgePoints += 100; break;
            case "Rare": computedBadgePoints += 250; break;
            case "Epic": computedBadgePoints += 500; break;
            case "Legendary": computedBadgePoints += 1000; break;
            default: computedBadgePoints += 50; break;
          }
        }
      });
    }

    parsed.total_points = Math.max(parsed.total_points || 0, computedBadgePoints);

    if (userId) {
      localStorage.setItem(cacheKey, JSON.stringify(parsed));
    }

    return parsed;
  } catch (e) {
    return defaultStats;
  }
}

export function saveUserStats(stats: UserStats, userId?: string): void {
  try {
    const cacheKey = userId ? `biznearyou_user_stats_${userId}` : "biznearyou_user_stats_v1";
    localStorage.setItem(cacheKey, JSON.stringify(stats));
  } catch (e) {
    console.error("Failed to save user stats:", e);
  }
}

export function clearUserStatsCache(userId?: string): void {
  try {
    if (userId) {
      localStorage.removeItem(`biznearyou_user_stats_${userId}`);
    }
    localStorage.removeItem("biznearyou_user_stats_v1");
  } catch (e) {
    console.error("Failed to clear user stats cache:", e);
  }
}

/**
 * Updates a specific daily goal progress and handles achievement checks
 */
export function incrementDailyGoal(type: keyof UserStats['dailyGoals'], amount: number = 1): UserStats {
  const stats = getUserStats();
  stats.dailyGoals[type] += amount;
  
  // Track lifetime stats
  if (!stats.lifetime_searches) stats.lifetime_searches = 0;
  if (!stats.lifetime_saves) stats.lifetime_saves = 0;
  if (!stats.lifetime_contacts) stats.lifetime_contacts = 0;
  if (!stats.lifetime_audits) stats.lifetime_audits = 0;

  if (type === "reviewed") {
    stats.lifetime_searches += amount;
  } else if (type === "saved") {
    stats.lifetime_saves += amount;
  } else if (type === "contacted") {
    stats.lifetime_contacts += amount;
  } else if (type === "followUp") {
    stats.lifetime_audits += amount;
  }

  // Check achievements
  stats.achievements = stats.achievements.map(ach => {
    if (ach.unlockedAt) return ach;
    
    let shouldUnlock = false;
    
    if (ach.id === "first_save" && type === "saved" && stats.dailyGoals.saved >= 1) {
      shouldUnlock = true;
    }
    if (ach.id === "first_outreach" && type === "contacted" && stats.dailyGoals.contacted >= 1) {
      shouldUnlock = true;
    }
    if (ach.id === "high_signal" && type === "reviewed" && stats.dailyGoals.reviewed >= 10) {
      shouldUnlock = true;
    }
    if (ach.id === "streak_3" && stats.streak >= 3) {
      shouldUnlock = true;
    }
    if (ach.id === "streak_7" && stats.streak >= 7) {
      shouldUnlock = true;
    }
    if (ach.id === "streak_15" && stats.streak >= 15) {
      shouldUnlock = true;
    }
    if (ach.id === "streak_30" && stats.streak >= 30) {
      shouldUnlock = true;
    }
    if (ach.id === "crm_master" && type === "followUp" && stats.dailyGoals.followUp >= 5) {
      shouldUnlock = true;
    }
    if (ach.id === "perfect_day") {
      // Free quest goals: reviewed >= 5, saved >= 2, contacted >= 1
      if (stats.dailyGoals.reviewed >= 5 && stats.dailyGoals.saved >= 2 && stats.dailyGoals.contacted >= 1) {
        shouldUnlock = true;
      }
    }
    if (ach.id === "data_pro" && type === "contacted" && stats.dailyGoals.contacted >= 5) {
      shouldUnlock = true;
    }
    if (ach.id === "expansionist" && type === "saved" && stats.dailyGoals.saved >= 5) {
      shouldUnlock = true;
    }
    if (ach.id === "scouting_beast" && (stats.lifetime_searches || 0) >= 15) {
      shouldUnlock = true;
    }
    if (ach.id === "scouting_elite" && (stats.lifetime_searches || 0) >= 50) {
      shouldUnlock = true;
    }
    if (ach.id === "scouting_grandmaster" && (stats.lifetime_searches || 0) >= 150) {
      shouldUnlock = true;
    }
    if (ach.id === "hoarder" && (stats.lifetime_saves || 0) >= 25) {
      shouldUnlock = true;
    }
    if (ach.id === "hoarder_legend" && (stats.lifetime_saves || 0) >= 100) {
      shouldUnlock = true;
    }
    if (ach.id === "seo_specialist" && (stats.lifetime_audits || 0) >= 5) {
      shouldUnlock = true;
    }
    if (ach.id === "contact_mogul" && (stats.lifetime_contacts || 0) >= 5) {
      shouldUnlock = true;
    }
    if (ach.id === "outreach_champion" && (stats.lifetime_contacts || 0) >= 30) {
      shouldUnlock = true;
    }
    if (ach.id === "outreach_god" && (stats.lifetime_contacts || 0) >= 100) {
      shouldUnlock = true;
    }
    
    if (shouldUnlock) {
      return { ...ach, unlockedAt: new Date().toLocaleDateString() };
    }
    return ach;
  });

  // Check completionists
  checkCompletionists(stats);

  saveUserStats(stats);
  return stats;
}

export function unlockAchievement(id: string): UserStats {
  const stats = getUserStats();
  let updated = false;
  stats.achievements = stats.achievements.map(ach => {
    if (ach.id === id && !ach.unlockedAt) {
      updated = true;
      return { ...ach, unlockedAt: new Date().toLocaleDateString() };
    }
    return ach;
  });
  if (updated) {
    checkCompletionists(stats);
    saveUserStats(stats);
  }
  return stats;
}

/**
 * Tracks unique locations, total searches, late-night triggers, and filters used
 */
export function trackSearch(
  keyword: string,
  location: string,
  filtersUsed: boolean
): UserStats {
  const stats = getUserStats();
  
  if (!stats.lifetime_searches) stats.lifetime_searches = 0;
  stats.lifetime_searches += 1;

  // Track unique locations
  if (!stats.searched_locations) stats.searched_locations = [];
  const normalizedLoc = location.trim().toLowerCase();
  if (normalizedLoc && !stats.searched_locations.includes(normalizedLoc)) {
    stats.searched_locations.push(normalizedLoc);
  }

  // Check late night owl scout
  const currentHour = new Date().getHours();
  const isLateNight = currentHour >= 0 && currentHour < 4;

  stats.achievements = stats.achievements.map(ach => {
    if (ach.unlockedAt) return ach;
    
    let shouldUnlock = false;
    
    if (ach.id === "quick_scan") {
      shouldUnlock = true;
    }
    if (ach.id === "scouting_beast" && stats.lifetime_searches >= 15) {
      shouldUnlock = true;
    }
    if (ach.id === "scouting_elite" && stats.lifetime_searches >= 50) {
      shouldUnlock = true;
    }
    if (ach.id === "scouting_grandmaster" && stats.lifetime_searches >= 150) {
      shouldUnlock = true;
    }
    if (ach.id === "pioneer" && stats.searched_locations!.length >= 3) {
      shouldUnlock = true;
    }
    if (ach.id === "filter_ninja" && filtersUsed) {
      shouldUnlock = true;
    }
    if (ach.id === "secret_night_owl" && isLateNight) {
      shouldUnlock = true;
    }

    if (shouldUnlock) {
      return { ...ach, unlockedAt: new Date().toLocaleDateString() };
    }
    return ach;
  });

  checkCompletionists(stats);
  saveUserStats(stats);
  return stats;
}

/**
 * Track details of business being viewed (Hiring, website status, SSL risks, speedrun views)
 */
export function trackBusinessDetailView(business: any): void {
  const stats = getUserStats();

  if (!stats.lifetime_views) stats.lifetime_views = 0;
  stats.lifetime_views += 1;

  const bTags = normalizeTags(business.opportunityTags);

  const hasHighHiring = !!(
    business.highHiringSignal || 
    (business.scores?.hiringScore && business.scores.hiringScore > 65) || 
    business.hiringStatus === "hiring" ||
    bTags.includes("Likely Hiring Category") ||
    bTags.includes("Careers Page Found") ||
    bTags.includes("Seasonal Hiring Likely")
  );

  const hasCareersPage = !!(
    business.hiringPageUrl || 
    business.websiteAudit?.hiringPageUrl || 
    bTags.includes("Careers Page Found")
  );

  const hasNoWebsite = !business.website || bTags.includes("No Website Found");
  
  const hasSslRisk = !!(
    business.websiteAudit?.warnings?.some((w: string) => w.toLowerCase().includes("ssl") || w.toLowerCase().includes("security")) ||
    bTags.includes("SSL Security Risk") ||
    bTags.includes("Website Needs Review")
  );

  const score = business.score || business.scores?.overallScore || 0;

  if (hasHighHiring) {
    if (!stats.lifetime_hiring_signals) stats.lifetime_hiring_signals = 0;
    stats.lifetime_hiring_signals += 1;
  }
  if (hasCareersPage) {
    if (!stats.lifetime_careers_pages) stats.lifetime_careers_pages = 0;
    stats.lifetime_careers_pages += 1;
  }
  if (hasNoWebsite) {
    if (!stats.lifetime_no_website) stats.lifetime_no_website = 0;
    stats.lifetime_no_website += 1;
  }
  if (hasSslRisk) {
    if (!stats.lifetime_ssl_risks) stats.lifetime_ssl_risks = 0;
    stats.lifetime_ssl_risks += 1;
  }

  // Speedrun tracking
  if (!stats.session_views_timestamps) stats.session_views_timestamps = [];
  const now = Date.now();
  stats.session_views_timestamps.push(now);
  if (stats.session_views_timestamps.length > 5) {
    stats.session_views_timestamps.shift();
  }
  const isSpeedrun = stats.session_views_timestamps.length === 5 && (now - stats.session_views_timestamps[0] <= 60000);

  stats.achievements = stats.achievements.map(ach => {
    if (ach.unlockedAt) return ach;

    let shouldUnlock = false;

    if (ach.id === "curious_scout") {
      shouldUnlock = true;
    }
    if (ach.id === "hiring_sleuth" && (stats.lifetime_hiring_signals || 0) >= 5) {
      shouldUnlock = true;
    }
    if (ach.id === "careers_finder" && (stats.lifetime_careers_pages || 0) >= 3) {
      shouldUnlock = true;
    }
    if (ach.id === "digital_rescuer" && (stats.lifetime_no_website || 0) >= 10) {
      shouldUnlock = true;
    }
    if (ach.id === "secure_scout" && (stats.lifetime_ssl_risks || 0) >= 5) {
      shouldUnlock = true;
    }
    if (ach.id === "secret_jackpot" && score >= 95) {
      shouldUnlock = true;
    }
    if (ach.id === "secret_speedrun" && isSpeedrun) {
      shouldUnlock = true;
    }

    if (shouldUnlock) {
      return { ...ach, unlockedAt: new Date().toLocaleDateString() };
    }
    return ach;
  });

  checkCompletionists(stats);
  saveUserStats(stats);
}

export function trackPhoneCallClick(): void {
  const stats = getUserStats();
  if (!stats.lifetime_phone_calls) stats.lifetime_phone_calls = 0;
  stats.lifetime_phone_calls += 1;

  stats.achievements = stats.achievements.map(ach => {
    if (ach.unlockedAt) return ach;
    if (ach.id === "phone_dialer" && stats.lifetime_phone_calls! >= 5) {
      return { ...ach, unlockedAt: new Date().toLocaleDateString() };
    }
    return ach;
  });

  checkCompletionists(stats);
  saveUserStats(stats);
}

export function trackDirectionsRequest(): void {
  const stats = getUserStats();
  if (!stats.lifetime_directions) stats.lifetime_directions = 0;
  stats.lifetime_directions += 1;

  stats.achievements = stats.achievements.map(ach => {
    if (ach.unlockedAt) return ach;
    if (ach.id === "direction_scout" && stats.lifetime_directions! >= 5) {
      return { ...ach, unlockedAt: new Date().toLocaleDateString() };
    }
    return ach;
  });

  checkCompletionists(stats);
  saveUserStats(stats);
}

export function trackCopyClick(): void {
  const stats = getUserStats();
  if (!stats.lifetime_copies) stats.lifetime_copies = 0;
  stats.lifetime_copies += 1;

  stats.achievements = stats.achievements.map(ach => {
    if (ach.unlockedAt) return ach;
    if (ach.id === "copy_ninja" && stats.lifetime_copies! >= 10) {
      return { ...ach, unlockedAt: new Date().toLocaleDateString() };
    }
    return ach;
  });

  checkCompletionists(stats);
  saveUserStats(stats);
}

export function trackQuestCompletion(): void {
  const stats = getUserStats();
  if (!stats.lifetime_quests_completed) stats.lifetime_quests_completed = 0;
  stats.lifetime_quests_completed += 1;

  stats.achievements = stats.achievements.map(ach => {
    if (ach.unlockedAt) return ach;
    if (ach.id === "quest_conqueror" && stats.lifetime_quests_completed! >= 5) {
      return { ...ach, unlockedAt: new Date().toLocaleDateString() };
    }
    if (ach.id === "quest_legend" && stats.lifetime_quests_completed! >= 20) {
      return { ...ach, unlockedAt: new Date().toLocaleDateString() };
    }
    return ach;
  });

  checkCompletionists(stats);
  saveUserStats(stats);
}

export function trackBoosterMinutes(minutes: number): void {
  const stats = getUserStats();
  if (minutes >= 120) {
    stats.achievements = stats.achievements.map(ach => {
      if (ach.id === "booster_legend" && !ach.unlockedAt) {
        return { ...ach, unlockedAt: new Date().toLocaleDateString() };
      }
      return ach;
    });
    checkCompletionists(stats);
    saveUserStats(stats);
  }
}

export function checkCompletionists(stats: UserStats): void {
  const completionistIds = ["badge_master", "completionist_epic", "completionist_legend"];
  const unlockedCount = stats.achievements.filter(ach => ach.unlockedAt && !completionistIds.includes(ach.id)).length;

  stats.achievements = stats.achievements.map(ach => {
    if (ach.unlockedAt) return ach;

    let shouldUnlock = false;
    if (ach.id === "badge_master" && unlockedCount >= 10) {
      shouldUnlock = true;
    }
    if (ach.id === "completionist_epic" && unlockedCount >= 20) {
      shouldUnlock = true;
    }
    if (ach.id === "completionist_legend" && unlockedCount >= 35) {
      shouldUnlock = true;
    }

    if (shouldUnlock) {
      return { ...ach, unlockedAt: new Date().toLocaleDateString() };
    }
    return ach;
  });
}

export function getAchievementProgressValue(id: string, stats: any): number {
  if (!stats) return 0;
  switch (id) {
    case "streak_3":
    case "streak_7":
    case "streak_15":
    case "streak_30":
      return stats.streak || 0;
    case "high_signal":
      return stats.dailyGoals?.reviewed || 0;
    case "crm_master":
      return stats.dailyGoals?.followUp || 0;
    case "data_pro":
      return stats.dailyGoals?.contacted || 0;
    case "expansionist":
      return stats.dailyGoals?.saved || 0;
    case "pioneer":
      return Array.isArray(stats.searched_locations) ? stats.searched_locations.length : 0;
    case "scouting_beast":
    case "scouting_elite":
    case "scouting_grandmaster":
      return stats.lifetime_searches || 0;
    case "hoarder":
    case "hoarder_legend":
      return stats.lifetime_saves || 0;
    case "seo_specialist":
      return stats.lifetime_audits || 0;
    case "contact_mogul":
    case "outreach_champion":
    case "outreach_god":
      return stats.lifetime_contacts || 0;
    case "hiring_sleuth":
      return stats.lifetime_hiring_signals || 0;
    case "careers_finder":
      return stats.lifetime_careers_pages || 0;
    case "digital_rescuer":
      return stats.lifetime_no_website || 0;
    case "secure_scout":
      return stats.lifetime_ssl_risks || 0;
    case "copy_ninja":
      return stats.lifetime_copies || 0;
    case "phone_dialer":
      return stats.lifetime_phone_calls || 0;
    case "direction_scout":
      return stats.lifetime_directions || 0;
    case "quest_conqueror":
    case "quest_legend":
      return stats.lifetime_quests_completed || 0;
    case "badge_master":
    case "completionist_epic":
    case "completionist_legend":
      return Array.isArray(stats.achievements)
        ? stats.achievements.filter((a: any) => a.unlockedAt && !["badge_master", "completionist_epic", "completionist_legend"].includes(a.id)).length
        : 0;
    default:
      return 0;
  }
}

/**
 * Track user interactions and learn category preferences
 */
export function trackInteraction(
  businessId: string, 
  category: string, 
  eventType: Interaction['eventType'],
  business?: any
): void {
  try {
    const listData = localStorage.getItem("biznearyou_interactions_v1") || "[]";
    const interactions: Interaction[] = JSON.parse(listData);
    
    const newInteraction: Interaction = {
      id: Math.random().toString(36).substring(2, 9),
      businessId,
      category: category || "Other",
      eventType,
      timestamp: Date.now()
    };
    
    interactions.push(newInteraction);
    // Keep last 150 interactions to keep it compact
    if (interactions.length > 150) {
      interactions.shift();
    }
    
    localStorage.setItem("biznearyou_interactions_v1", JSON.stringify(interactions));
    
    // Update Daily Goals
    if (eventType === "view") {
      incrementDailyGoal("reviewed", 1);
      if (business) {
        trackBusinessDetailView(business);
      }
    } else if (eventType === "save") {
      incrementDailyGoal("saved", 1);
    } else if (eventType === "contact_click" || eventType === "website_click") {
      incrementDailyGoal("contacted", 1);
    } else if (eventType === "pipeline_change") {
      incrementDailyGoal("followUp", 1);
    }
  } catch (e) {
    console.error("Failed to track interaction:", e);
  }
}

/**
 * Computes preferred categories based on interactions
 */
export function getPreferredCategories(): Record<string, number> {
  try {
    const listData = localStorage.getItem("biznearyou_interactions_v1") || "[]";
    const interactions: Interaction[] = JSON.parse(listData);
    
    const weights = {
      'view': 1,
      'website_click': 2,
      'save': 3,
      'contact_click': 4,
      'pipeline_change': 3,
      'unsave': -3,
    };
    
    const scores: Record<string, number> = {};
    
    interactions.forEach(item => {
      if (!item.category || item.category === "Other") return;
      const weight = weights[item.eventType] || 1;
      scores[item.category] = (scores[item.category] || 0) + weight;
    });
    
    return scores;
  } catch (e) {
    return {};
  }
}

/**
 * Personalizes a business score based on user interest
 */
export function getPersonalizedScore(business: BusinessBusiness, baseScore: number): number {
  if (!business.category) return baseScore;
  const prefs = getPreferredCategories();
  
  const scoreFactor = prefs[business.category] || 0;
  // Boost by up to 15 points based on interest weight
  const boost = Math.min(15, Math.max(0, scoreFactor * 1.5));
  
  return Math.min(100, Math.round(baseScore + boost));
}

/**
 * Explanation of why this is recommended
 */
export function getPersonalizedExplanation(business: BusinessBusiness): string | null {
  if (!business.category) return null;
  const prefs = getPreferredCategories();
  const scoreFactor = prefs[business.category] || 0;
  
  if (scoreFactor >= 5) {
    return `Recommended: You have shown heavy interest in local "${business.category}" opportunities.`;
  }
  if (scoreFactor >= 2) {
    return `Suggested: Based on your recent views of "${business.category}" businesses.`;
  }
  return null;
}

/**
 * Professional outreach email templates tailored to target types
 */
export function getEmailTemplates(
  businessName: string,
  category: string,
  role: string,
  score: number,
  contactName?: string
): { id: string; subject: string; body: string; label: string }[] {
  const title = contactName || "Hiring Manager";
  const cat = category || "local agency";

  return [
    {
      id: "role_inquiry",
      label: "💼 Job / Internship Inquiry",
      subject: `Inquiry: Open Opportunities at ${businessName}`,
      body: `Hi ${title},\n\nI hope this email finds you well.\n\nI have been following ${businessName}’s impressive standing in the community as a top-ranked local ${cat}. I’m highly interested in supporting your team, specifically with potential roles like ${role} or general operational operations.\n\nI’d love to learn if you are currently reviewing candidates or open to a brief chat. I can share my qualifications or drop by briefly if that works better!\n\nBest regards,\n[Your Name]\n[Your Contact Info]`
    },
    {
      id: "digital_optimization",
      label: "🚀 Website & Local Marketing Offer",
      subject: `Complimentary Local Strategy Ideas for ${businessName}`,
      body: `Hi ${title},\n\nI was reviewing local ${cat} businesses in our neighborhood and was highly impressed by ${businessName}’s outstanding customer reviews! Your local reputation is fantastic.\n\nHowever, I noticed a couple of minor digital gaps on your public listing (such as online reservation options and responsive layouts) that might be causing you to lose interested clients to competitors.\n\nI put together a couple of quick, actionable ideas to automate your bookings and secure more client reviews. I’d love to send them over for free if you are interested!\n\nBest regards,\n[Your Name]\n[Your Website / Agency Info]`
    },
    {
      id: "quick_intro",
      label: "👋 Quick Introduction Match",
      subject: `Quick greeting from a local neighbor: ${businessName}`,
      body: `Hi ${title},\n\nMy name is [Your Name], and I live right here nearby. I’ve noticed ${businessName}’s fantastic presence in the area and wanted to reach out directly.\n\nI specialize in helping local ${cat} operations streamline client communications, secure Google reviews, and manage local ads. If you are ever looking to add support or delegate tasks, I would love to be a resource for your business.\n\nWould you be open to a 5-minute introduction next week?\n\nSincerely,\n[Your Name]\n[Your Phone Number]`
    }
  ];
}
